import { Component, ElementRef, Input, ViewChild } from '@angular/core';
import { DeviceDetailComponent } from '../device-detail/device-detail.component';
import { MatDialog } from '@angular/material/dialog';
import {
  DetailOpenMode,
  DeviceTypeAttr,
  DeviceTypeKeys,
  DeviceTypes,
} from '../shared/models/device-types-attributes';
import { MatTooltip } from '@angular/material/tooltip';
import {
  DeviceCardItem,
  DeviceListItem,
  DeviceStatus,
  DeviceStatusAttr,
  DeviceStatusData,
} from '../shared/models/device-list.model';
import { FormControl, FormGroup } from '@angular/forms';
import {
  LogisticStatusValues,
  UpdateDeviceLogisticResponse,
} from '../shared/models/device.model';
import { animate, style, transition, trigger } from '@angular/animations';
import { DeviceService } from '../shared/services/device.service';
import { Observable, map, startWith, pipe } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from "@ngx-translate/core";
import { downloadPdfFromBase64 } from "../../shared/utils/pdf";
import {PdfSelectionDialogComponent} from "../../pdf/pdf-selection-dialog/pdf-selection-dialog.component";


@Component({
  selector: 'app-device-card',
  templateUrl: './device-card.component.html',
  styleUrls: [
    './device-card.component.scss',
    './device-card-by-type.scss',
    './device-card-by-status.scss',
  ],
  animations: [
    trigger('appear', [
      transition(':enter', [
        animate('0ms', style({opacity: 0})),
        animate('500ms ease-out', style({opacity: 1})),
      ]),
    ]),
  ],
})
export class DeviceCardComponent {
  @Input() device: DeviceCardItem | undefined;
  @Input() multiCustomer: boolean = false;

  @ViewChild('logInput', {read: ElementRef}) logInput!: ElementRef;
  @ViewChild('logDescArea', {read: ElementRef}) logDescArea!: ElementRef;

  protected loading = false;
  protected editMode = false;
  protected readonly editForm = new FormGroup({
    logisticStatus: new FormControl('', {nonNullable: true}),
    logisticStatusDesc: new FormControl('', {nonNullable: true}),
  });

  protected logValuesObservable: Observable<string[]>;

  constructor(
    private dialog: MatDialog,
    protected deviceService: DeviceService,
    private _snackBar: MatSnackBar,
    protected translate: TranslateService,
  ) {
    this.logValuesObservable = this.editForm
      .get('logisticStatus')
      ?.valueChanges.pipe(
        startWith(''),
        map((value) => (value ? this.filterLogValues(value) : this.LogValues))
      )!;
  }

  protected get deviceType(): DeviceTypeKeys {
    return this.device!.externalDeviceTypeCode;
  }

  protected get deviceTypeAttr(): DeviceTypeAttr {
    return DeviceTypes[this.deviceType];
  }

  protected openDetails(
    event: Event,
    typeCode = DeviceTypeKeys.NONE,
    openMode = DetailOpenMode.REGULAR
  ): void {
    this.closeEdit(event);
    event.stopPropagation();
    const data: DeviceListItem = {
      ...this.device,
      externalDeviceTypeCode: typeCode,
    };
    const dialogRef = this.dialog.open(DeviceDetailComponent, {
      panelClass: ['mat-dialog-override', 'dialog-responsive'],
      data: {
        itemData: data,
        openMode,
      },
    });
    dialogRef.componentInstance.itemEditEventEmitter.subscribe({
      next: (data: UpdateDeviceLogisticResponse) => {
        if (this.device) {
          this.device.logisticStatus = data.logisticStatus;
          this.device.logisticStatusDesc = data.logisticStatusDesc;
        }
      },
    });
    dialogRef.afterClosed().subscribe({
      next: () => {
        dialogRef.componentInstance.itemEditEventEmitter.unsubscribe();
      },
    });
  }

  protected openEdit(event: Event, focus: 'logDescArea' | 'logInput'): void {
    event.stopPropagation();
    this.iconHovered = false;
    this.editForm.patchValue({
      logisticStatus: this.device?.logisticStatus,
      logisticStatusDesc: this.device?.logisticStatusDesc,
    });
    this.editMode = true;
    setTimeout(() => {
      this[focus]?.nativeElement.focus();
    }, 0);
  }

  protected closeEdit(event: Event, save = false): void {
    event.stopPropagation();
    this.iconHovered = false;
    const needToSave =
      save &&
      Object.keys(this.editForm.controls).some(
        (key) =>
          this.editForm.get(key)?.value !==
          this.device?.[key as keyof DeviceCardItem]
      );
    if (needToSave) {
      this.loading = true;
      const {logisticStatus, logisticStatusDesc} = this.editForm.value;
      this.deviceService
        .updateLogisticStatus({
          deviceId: this.device!.deviceId,
          externalDeviceId: this.device!.externalDeviceId,
          logisticStatus,
          logisticStatusDesc,
        })
        .subscribe({
          next: (res) => {
            this.device!.logisticStatus = res.logisticStatus;
            this.device!.logisticStatusDesc = res.logisticStatusDesc;
          },
        })
        .add(() => {
          this.editMode = false;
          this.loading = false;
        });
    } else {
      this.editMode = false;
    }
  }

  protected copyClick(event: Event, tooltip: MatTooltip): void {
    event.stopImmediatePropagation();
    tooltip.show();
  }

  protected copyHover(event: Event): void {
    event.stopImmediatePropagation();
    this.iconHovered = true;
  }

  protected copyHoverEnd(event: Event, tooltip: MatTooltip): void {
    event.stopImmediatePropagation();
    this.iconHovered = false;
    tooltip.hide();
  }

  protected get status(): DeviceStatus | undefined {
    return this.device!.operationStatus;
  }

  protected get statusData(): DeviceStatusAttr | undefined {
    return this.status ? DeviceStatusData[this.status] : undefined;
  }

  protected readonly Keys = DeviceTypeKeys;
  protected readonly Types = DeviceTypes;
  protected readonly Mode = DetailOpenMode;
  protected iconHovered = false;
  protected readonly LogValues = LogisticStatusValues;

  private filterLogValues(value: string): string[] {
    const lowerValue = value.toLowerCase();
    return this.LogValues.filter(
      (item) => value && item.toLowerCase().includes(lowerValue)
    );
  }

  protected clearFilter(name: string, $event: MouseEvent) {
    $event.stopPropagation();
    this.editForm.get(name)?.setValue('');
  }

  protected emptyFilter(name: string): boolean {
    const item = this.editForm.get(name);
    return item?.value === '' || Boolean(item?.disabled);
  }

  protected get canEdit(): boolean {
    return (
      this.device?.externalDeviceTypeCode === DeviceTypeKeys.SZT &&
      Boolean(this.device?.writeRight)
    );
  }

  protected isDownloading = false;

  downloadDatasheet(event: Event) {
    event.stopPropagation();

    this.deviceService.getDevicesV2().subscribe(response => {
      const dialogRef = this.dialog.open(PdfSelectionDialogComponent, {
        width: '700px',
        data: {
          pdfs: response.pdfs,
          sztExtDeviceId: this.device?.externalDeviceId ?? '',
        }
      });

      dialogRef.afterClosed().subscribe(pdfId => {
        if (pdfId) {
          this.isDownloading = true;
          this.deviceService.getDeviceDatasheetLink(pdfId)
            .subscribe({
              next: (res) => {
                downloadPdfFromBase64(res.encodedPdf, this.device!.externalDeviceId, true);
                this._snackBar.open(this.translate.instant('datasheet-success'), '', {
                  duration: 2000
                });
              },
              error: () => {
                this._snackBar.open(this.translate.instant('datasheet-error'), '', {
                  duration: 2000
                });
              },
              complete: () => {
                this.isDownloading = false;
              }
            });
        }
      });
    });
  }
}
